import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { LeftArrowWhite } from '../../assets/images/svg/LeftArrow'
import { observer } from 'mobx-react'
import ConfirmaitonCodeInput from 'src/components/ui/ConfirmaitonCodeInput'
import { applicationStore } from 'src/store/ApplicationStore'
import Countdown from 'react-countdown'
import { toast } from 'react-toastify'
import Preloader from 'src/components/ui/Preloader'
import { workflowStepIterator } from 'src/services/workflowStepIterator'
import { NodeTypes } from 'src/enums/NodeTypes'
import { parseServiceErrorMessage } from 'src/helpers/parseServiceError'
import { useTranslation } from 'react-i18next'

const ConfirmPhone = observer(() => {
    const location = useLocation();
    const { phoneValue } = location?.state;
    const navigate = useNavigate();

    const [isActive, setIsActive] = useState(false);
    const [value, setValue] = useState(null);
    const [time, setTime] = useState(Date.now() + 60000);
    const [submitting, setSubmitting] = useState(false);
    const [resending, setResending] = useState(false);
    const [countdownCompleted, setCountdownCompleted] = useState(false);
    const { t } = useTranslation();

    const renderer = ({ seconds, completed }) => {
        if (completed) {
            setIsActive(false);
            setCountdownCompleted(true);
            return <div className="text-body text-Gray900">{t('confirmPhone.expired')}</div>;
        } else {
            return <div className="text-body text-Gray900">{t('confirmPhone.enterWithinSeconds', { seconds: seconds === 0 ? 60 : seconds })}</div>;
        }
    };

    const handleNextPage = async () => {
        if (isActive) {
            try {
                setSubmitting(true);
                const response = await workflowStepIterator({
                    access_token: applicationStore.access_token,
                    node_id: applicationStore.workflowNextServiceResponse.node_id,
                    body: {
                        "code": value
                    },
                    action_index: 1,
                    constextOfStep: { step: NodeTypes.PHONE_VERIFICATION, "retry": applicationStore.workflowNextServiceResponse.retry }
                })

                if (response.data?.service_results[0].status !== 200) {
                    const rawMessage = response.data?.service_results[0]?.message;
                    const parsedMessage = parseServiceErrorMessage(rawMessage);
                    throw new Error(parsedMessage);
                }
                applicationStore.setWorkflowNextServiceResponse(response.data);
                applicationStore.setConditionContext({ "phone": phoneValue })
                navigate("/next", { replace: true });

            } catch (error) {
                toast.error(error?.message || t('confirmPhone.notConfirmed'));
            } finally {
                setSubmitting(false);
            }
        }
    };

    const handleSendPhone = async () => {
        // Check if countdown is still active
        if (!countdownCompleted) {
            toast.warning(t('confirmPhone.waitForCountdown'));
            return;
        }

        try {
            setResending(true);
            const response = await workflowStepIterator({
                access_token: applicationStore.access_token,
                node_id: applicationStore.workflowNextServiceResponse.node_id,
                body: {
                    "phone": phoneValue
                },
                action_index: 0,
                constextOfStep: { step: NodeTypes.PHONE_VERIFICATION }
            })
            if (response.data?.service_results[0].status !== 200) {
                const rawMessage = response.data?.service_results[0]?.message;
                const parsedMessage = parseServiceErrorMessage(rawMessage);
                throw new Error(parsedMessage);
            }

            setCountdownCompleted(false);
            toast.success(t('confirmPhone.messageSent'));
            setTime(Date.now() + 60000);

        } catch (error) {
            toast.error(error?.message || t('confirmPhone.messageNotSent'));
        } finally {
            setResending(false);
        }
    };

    return (
        <div className="custom-container">
            <div className="flex justify-between flex-col items-center h-full">
                <div>
                    <div className='text-left'>
                        <Button className="" variant="primaryBack" onClick={() => { navigate("/phone-verification", { replace: true }) }}><LeftArrowWhite /></Button>
                    </div>
                    <div className='grid gap-[16px] mt-10'>
                        <div className="text-h5 font-bold text-flow-heading">{t('confirmPhone.title')}</div>
                        <div className="text-body text-Gray900" dangerouslySetInnerHTML={{ __html: t('confirmPhone.sentTo', { phone: `<span class='font-bold'>${phoneValue}</span>` }) }} />
                        <Countdown key={time} date={time} renderer={renderer} />
                    </div>
                    <div className='mt-10'>
                        <ConfirmaitonCodeInput setIsActive={setIsActive} setValue={setValue} />
                    </div>
                    <div className='mt-10'>
                        <Button
                            className="mt-4"
                            variant="primaryWithoutIcon"
                            disabled={!isActive || submitting}
                            onClick={handleNextPage}
                        >
                            <span className="me-1">{t('actions.continue')}</span>
                            {submitting && <Preloader variant='white' />}
                        </Button>
                        <Button
                            className='mt-3'
                            variant="secondaryWithoutIcon"
                            disabled={resending || !countdownCompleted}
                            onClick={handleSendPhone}
                        >
                            <span className="me-1">{t('confirmPhone.resendCode')}</span>
                            {resending && <Preloader variant="gray" />}
                        </Button>
                    </div>
                </div>
                <div className="">
                </div>
            </div>
        </div>
    )
})

export default ConfirmPhone
